import { Box, Divider, Grid, Popover, Typography } from "@mui/material";
import useColors from "../../../../api/hooks/useColors";
import { icons } from "../../../stack/environment/icons/iconExport";
import ChipColor from "./ChipColor";
import ChipTag from "./ChipTag";

const RemediationBreakdown = ({
  relatedControl,
  anchorEl,
  onClose,
  selectedRemediation,
}) => {
  const colors = useColors();
  const open = Boolean(anchorEl);
  const id = open ? "feasibility-popover" : undefined;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "center" }}
      style={{ zIndex: 1500 }}
      PaperProps={{
        style: { borderRadius: 10, background: colors.common_white }, // Change the value as needed
      }}
    >
      <Box
        sx={{
          margin: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1">Remediation Breakdown</Typography>
          <ChipColor
            value={selectedRemediation?.priority}
            type="remediationPriority"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 3,
          }}
        >
          <Box
            sx={{
              minWidth: "240px",

              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            {selectedRemediation && (
              <Grid container direction={"column"} gap={1}>
                <Grid
                  container
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <Grid item xs={6}>
                    <Typography variant="subtitle1">Feasibility</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <ChipColor
                      value={selectedRemediation.feasibility}
                      type="feasibility"
                      labelWidth={true}
                    ></ChipColor>
                  </Grid>
                </Grid>
                <Divider orientation="horizontal" flexItem />

                <Box
                  marginTop={"8px"}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    justifyItems: "baseline",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    textAlign={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="caption">Complexity</Typography>
                    <ChipColor
                      value={selectedRemediation?.complexity}
                      type="complexity"
                      defaultColor={true}
                      size="small"
                      labelWidth={true}
                    />
                  </Box>

                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    textAlign={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="caption">UX Effect</Typography>
                    <ChipColor
                      value={selectedRemediation.impact_to_ux}
                      size="small"
                      labelWidth={true}
                    />
                  </Box>

                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    textAlign={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="caption">Total Cost</Typography>
                    <ChipColor
                      value={"$" + selectedRemediation.total_cost}
                      size="small"
                      labelWidth={true}
                    />
                  </Box>

                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    textAlign={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="caption">Effort</Typography>
                    <ChipColor
                      value={selectedRemediation.estimated_effort + " Days"}
                      size="small"
                      labelWidth={true}
                    />
                  </Box>

                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    textAlign={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="caption">Duration</Typography>
                    <ChipColor
                      value={selectedRemediation.duration + " Days"}
                      size="small"
                      labelWidth={true}
                    />
                  </Box>
                </Box>
              </Grid>
            )}
          </Box>
          <Divider flexItem orientation="vertical" />
          {selectedRemediation && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                minWidth: "245px",
                height: "100%",
              }}
            >
              <Grid container>
                <Grid container direction={"column"} gap={1}>
                  <Grid
                    container
                    direction={"row"}
                    justifyItems={"baseline"}
                    justifyContent={"space-between"}
                  >
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">Impact</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <ChipColor
                        value={selectedRemediation.impact}
                        type="impact"
                        labelWidth={true}
                      ></ChipColor>
                    </Grid>
                  </Grid>
                  <Divider orientation="horizontal" flexItem />

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      justifyItems: "baseline",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      gap={2}
                      alignItems={"center"}
                      textAlign={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="caption">
                        Attack Likelihood
                      </Typography>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ChipColor
                          type={"likelihood"}
                          value={selectedRemediation?.likelihood}
                          size="small"
                          defaultColor={true}
                          labelWidth={true}
                        />
                      </div>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      gap={2}
                      alignItems={"center"}
                      textAlign={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="caption">Gap Severity</Typography>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ChipColor
                          type={"controlPriority"}
                          value={selectedRemediation.severity}
                          defaultColor={true}
                          size="small"
                          labelWidth={true}
                        />
                      </div>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      gap={2}
                      alignItems={"center"}
                      textAlign={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="caption" textAlign={"center"}>
                        Mitigated Steps
                      </Typography>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ChipColor
                          value={selectedRemediation.related_step_ids.length}
                          size="small"
                          labelWidth={true}
                        />
                      </div>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      gap={2}
                      alignItems={"center"}
                      textAlign={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="caption">Probability</Typography>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ChipColor
                          value={selectedRemediation.probability + "%"}
                          size="small"
                          labelWidth={true}
                        />
                      </div>
                    </Box>
                    {relatedControl?.misconfigurations_count > 0 && (
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        gap={2}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Typography
                          variant="caption"
                          textAlign={"center"}
                          fontSize={"12px"}
                        >
                          Misconfigured Entities
                        </Typography>
                        <ChipColor
                          value={relatedControl?.misconfigurations_count}
                          size="small"
                          labelWidth={true}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Box display={"flex"} gap={2} mt={"16px"} mb={"16px"}>
                  {selectedRemediation.exploitable && (
                    <ChipTag
                      iconSrc={icons.ExploitableIcon}
                      label={"Exploitable"}
                    ></ChipTag>
                  )}
                </Box>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </Popover>
  );
};

export default RemediationBreakdown;
